// src/Dashboard.js

import React, { useState, useEffect, useMemo } from "react";
import { db } from "./firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import Loader from "./Loader";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { AlertCircle, TrendingUp, Trophy, Sigma } from "lucide-react";
import {
  getPeriodBoundaries,
  computeProgress as rawComputeProgress,
} from "./utils/progress";

const MONTHS_SHORT = [
  "sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"
];
const MONTHS_FULL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const calcTypePL = {
  average: "Średnia (im więcej tym lepiej)",
  averageLow: "Średnia (im mniej tym lepiej)",
  quantityMin: "Ilość (suma wystąpień)",
  cap: "Suma wartości",
  maxValue: "Maksymalna wartość",
  daysMetThreshold: "Dni spełniające warunek",
  streak: "Passa"
};

const parseDateString = (s) =>
  s.includes(".") ? new Date(...s.split(".").reverse().map(Number)) : new Date(s);

const safeComputePercentage = (goal, period, progress) => {
  const cfg = goal?.[period];
  if (!cfg || cfg.opt == null || cfg.opt === 0) return 0;
  return Math.min(100, (progress / cfg.opt) * 100);
};

const computeProgress = (entries, goal, period, baseDate) => {
  if (!goal?.[period]) return 0;
  try {
    return rawComputeProgress(entries, goal, period, baseDate);
  } catch {
    return 0;
  }
};

function formatRange(start, end) {
  const opts = { year: "numeric", month: "long", day: "numeric" };
  return `${start.toLocaleDateString("pl-PL", opts)} – ${end.toLocaleDateString("pl-PL", opts)}`;
}

function getXTicks(tab, daysArr) {
  if (daysArr.length < 3) return daysArr.map(d => d.date);
  const idxs = [0, Math.floor(daysArr.length / 2), daysArr.length - 1];
  if (tab === "monthly") {
    return Array.from(new Set([0, 9, 19, daysArr.length - 1]))
      .filter(i => i >= 0 && i < daysArr.length)
      .map(i => daysArr[i].date);
  }
  return idxs.map(i => daysArr[i].date);
}
function formatXTicks(tab, d) {
  if (!d) return "";
  const date = new Date(d);
  if (tab === "weekly")
    return date.toLocaleDateString("pl-PL", { weekday: "short" }).replace(".", "");
  if (tab === "monthly")
    return date.getDate();
  if (tab === "yearly")
    return MONTHS_SHORT[date.getMonth()];
  return "";
}

// Detekcja trybu dark
function useIsDarkMode() {
  const [isDark, setIsDark] = useState(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
  );
  useEffect(() => {
    const match = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setIsDark(e.matches);
    match.addEventListener("change", onChange);
    const html = document.documentElement;
    const obs = new MutationObserver(() => {
      setIsDark(html.classList.contains("dark"));
    });
    obs.observe(html, { attributes: true, attributeFilter: ["class"] });
    return () => {
      match.removeEventListener("change", onChange);
      obs.disconnect();
    };
  }, []);
  return isDark;
}

// --------- DYNAMICZNE MAPOWANIE ---------
function buildMappings(goals) {
  const fieldMap = {};
  const typeMap = {};
  for (const g of goals) {
    fieldMap[g.question] = g.uid || g.field || g.name || g.question;
    typeMap[g.question] = g.calcType;
  }
  return { fieldMap, typeMap };
}

export default function Dashboard() {
  const [tab, setTab] = useState("weekly");
  const today = new Date();
  const isoDow = today.getDay() || 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - (isoDow - 1));
  const defMon = monday.toISOString().slice(0, 10);
  const defTod = today.toISOString().slice(0, 10);
  const [wStart, setWStart] = useState(defMon);
  const [wEnd, setWEnd] = useState(defTod);
  const currentMonthIdx = today.getMonth();
  const currentYear = today.getFullYear();
  const [mIdx, setMIdx] = useState(currentMonthIdx);
  const [mYr, setMYr] = useState(currentYear);
  const [ySel, setYSel] = useState(currentYear);

  const [goals, setGoals] = useState([]);
  const [sections, setSections] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- DYNAMICZNE MAPOWANIE POL I TYPÓW
  const [fieldMapping, setFieldMapping] = useState({});
  const [calcTypeMapping, setCalcTypeMapping] = useState({});

  const isDark = useIsDarkMode();

  useEffect(() => {
    (async () => {
      const gSnap = await getDoc(doc(db, "goals", "currentGoals"));
      if (gSnap.exists()) {
        setGoals(gSnap.data().goals);
        setSections(gSnap.data().sections || []);
      }
      const eSnap = await getDocs(collection(db, "entries"));
      const arr = [];
      eSnap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      setEntries(arr);
      setLoading(false);
    })();
  }, []);

  // MAPPING ZAWSZE GDY GOALS SIĘ ZMIENI
  useEffect(() => {
    if (goals && goals.length) {
      const { fieldMap, typeMap } = buildMappings(goals);
      setFieldMapping(fieldMap);
      setCalcTypeMapping(typeMap);
    }
  }, [goals]);

  const baseDate = useMemo(() => {
    if (tab === "weekly") return wEnd;
    if (tab === "monthly")
      return `${mYr}-${String(Number(mIdx) + 1).padStart(2, "0")}`;
    if (tab === "yearly") return String(ySel);
    return wEnd;
  }, [tab, wEnd, mYr, mIdx, ySel]);

  const { start, end } = useMemo(() => {
    if (tab === "weekly")
      return {
        start: new Date(`${wStart}T00:00`),
        end: new Date(`${wEnd}T23:59:59.999`),
      };
    if (tab === "monthly")
      return getPeriodBoundaries(tab, `${mYr}-${String(Number(mIdx) + 1).padStart(2, "0")}`);
    if (tab === "yearly")
      return getPeriodBoundaries(tab, String(ySel));
    return getPeriodBoundaries("weekly", wEnd);
  }, [tab, wStart, wEnd, mYr, mIdx, ySel]);

  const questionsBySection = useMemo(() => {
    const map = {};
    sections.forEach(sec => {
      map[sec] = goals.filter(g => g.category === sec && g[tab]);
    });
    return map;
  }, [goals, sections, tab]);

  if (loading) return <Loader />;

  const renderCard = (g) => {
    if (!g[tab]) return null;
    // --- UŻYWAJ DYNAMICZNYCH MAPOWAŃ:
    const field = fieldMapping[g.question] || g.uid || "";
    const displayCalcType = calcTypeMapping[g.question] || g.calcType || "cap";
    const progress = computeProgress(entries, g, tab, baseDate);
    const { min, opt } = g[tab];

    let achieved = false;
    let closeTo = false;

    if (displayCalcType === "averageLow") {
      if (progress <= opt) achieved = true;
      else if (progress > opt && progress <= min) closeTo = true;
    }
    else if (displayCalcType === "average") {
      if (progress >= opt) achieved = true;
      else if (progress < opt && progress >= min) closeTo = true;
    }
    else {
      if (progress >= opt) achieved = true;
      else if (progress < opt && progress >= min) closeTo = true;
    }

    const percentage =
      displayCalcType === "average" || displayCalcType === "averageLow"
        ? 0
        : safeComputePercentage(g, tab, progress);

    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push({ date: d.toISOString().slice(0, 10), progress: 0 });
    }
    entries.forEach((e) => {
      const d = e.createdAt?.toDate
        ? e.createdAt.toDate()
        : e.date
        ? parseDateString(e.date)
        : null;
      if (!d) return;
      const ds = d.toISOString().slice(0, 10);
      const pt = days.find((x) => x.date === ds);
      if (pt) pt.progress += typeof e[field] === "boolean" ? (e[field] ? 1 : 0) : Number(e[field]) || 0;
    });
    const xTicks = getXTicks(tab, days);

    // -- Motyw dark/light dla wykresu/tooltipa
    const chartStroke = isDark ? "#4ade80" : "#16a34a";
    const chartBg = isDark ? "#101928" : "#fff";
    const chartBorder = isDark ? "#22d3ee" : "#22c55e";
    const tooltipText = isDark ? "#fff" : "#134e17";

    return (
      <div
        key={g.id}
        className={
          `relative p-4 mb-8 rounded-xl border transition shadow-sm
           ${achieved
            ? "border-green-700 bg-green-50 dark:bg-green-900/40 shadow-[0_0_12px_0_rgba(34,197,94,0.14)]"
            : closeTo
            ? "border-yellow-400 bg-yellow-50 dark:bg-yellow-900/40 shadow-[0_0_12px_0_rgba(252,211,77,0.14)]"
            : "border-green-200 bg-white dark:bg-gray-900"}`
        }
        style={{ minWidth: 0 }}
      >
        <div className="flex flex-col gap-2">
          {/* BADGES */}
          {achieved && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-2 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow z-10">
                <Trophy size={16} className="text-yellow-200" /> Osiągnięto cel!
              </span>
            </div>
          )}
          {!achieved && closeTo && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-2 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold animate-bounce shadow z-10">
                <TrendingUp size={16} className="text-white" /> Już blisko! Minimalny poziom osiągnięty
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            {achieved
              ? <Trophy size={26} className="text-yellow-400" />
              : closeTo
              ? <TrendingUp size={26} className="text-yellow-500" />
              : <AlertCircle size={26} className="text-red-400" />}
            <span className="text-base font-semibold text-green-900 dark:text-green-200">{g.question}</span>
            <span className="ml-auto flex gap-2 items-center">
              <span className="bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-100 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                <Sigma size={13} className="opacity-70" />
                {calcTypePL[displayCalcType] || displayCalcType}
              </span>
            </span>
          </div>
          {/* MINI WYKRES */}
          <div className="h-16 w-full" style={{ minWidth: 0 }}>
            <ResponsiveContainer>
              <LineChart data={days}>
                <XAxis
                  dataKey="date"
                  ticks={xTicks}
                  tickFormatter={d => formatXTicks(tab, d)}
                  minTickGap={12}
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 12, right: 12 }}
                  tick={{
                    fill: isDark ? "#cbd5e1" : "#134e17",
                    fontWeight: 500,
                  }}
                />
                <YAxis hide domain={['dataMin', 'dataMax']} />
                <Line type="monotone" dataKey="progress" stroke={chartStroke} dot={false} strokeWidth={2.2} />
                <RechartsTooltip
                  isAnimationActive={false}
                  wrapperStyle={{ zIndex: 50 }}
                  contentStyle={{
                    background: chartBg,
                    color: tooltipText,
                    borderRadius: 12,
                    border: `1.5px solid ${chartBorder}`,
                    fontSize: 14,
                    boxShadow: "0 3px 18px 0 rgba(0,0,0,0.16)",
                  }}
                  labelFormatter={d => {
                    const date = new Date(d);
                    if (tab === "weekly" || tab === "monthly")
                      return `Data: ${date.toLocaleDateString("pl-PL", { day: "2-digit", month: "short" })}`;
                    return `Miesiąc: ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}`;
                  }}
                  formatter={v => [
                    `Wartość: ${v}`,
                    achieved
                      ? "Brawo! Osiągnięto cel! 🎉"
                      : closeTo
                      ? "Minimalny poziom przekroczony, kontynuuj!"
                      : ""
                  ]}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* INFO */}
          <div className="flex gap-2 text-sm text-gray-500 dark:text-gray-400 mt-2">
            <span>
              Min: <span className="text-red-500 dark:text-red-400">{min}</span>
            </span>
            <span>
              Opt: <span className="text-green-500 dark:text-green-300">{opt}</span>
            </span>
            {displayCalcType !== "average" && displayCalcType !== "averageLow" && (
              <span className="flex-1">
                <span className="block w-full h-3 mt-2 bg-green-100 dark:bg-green-900/20 rounded-full relative overflow-hidden">
                  <span
                    className="h-3 rounded-full absolute left-0 top-0"
                    style={{
                      width: `${percentage}%`,
                      background: `linear-gradient(90deg, #ef4444 0%, #f59e42 45%, #16a34a 100%)`,
                      transition: "width .4s"
                    }}
                  />
                </span>
              </span>
            )}
          </div>
          <div className="text-base font-bold text-green-700 dark:text-green-300 mt-2">
            {displayCalcType === "average" || displayCalcType === "averageLow"
              ? <>Średnia: {progress.toFixed(1)}</>
              : <>
                {displayCalcType === "cap" ? "Suma wartości" : "Bieżący"}: {progress} / {opt} ({Math.round(percentage)}%)
              </>
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-center text-green-700 dark:text-green-300 mb-3">
        Dashboard Postępów
      </h1>
      <div className="mb-2 flex flex-wrap items-center justify-center gap-3">
        {tab === "weekly" && (
          <>
            <label className="text-green-700 dark:text-green-300 font-medium">Od:</label>
            <input
              type="date"
              value={wStart}
              max={wEnd}
              onChange={e => setWStart(e.target.value)}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            />
            <label className="text-green-700 dark:text-green-300 font-medium">Do:</label>
            <input
              type="date"
              value={wEnd}
              min={wStart}
              max={today.toISOString().slice(0, 10)}
              onChange={e => setWEnd(e.target.value)}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            />
          </>
        )}
        {tab === "monthly" && (
          <>
            <label className="text-green-700 dark:text-green-300 font-medium">Miesiąc:</label>
            <select
              value={mIdx}
              onChange={e => setMIdx(Number(e.target.value))}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            >
              {MONTHS_FULL.map((name, idx) => (
                <option key={idx} value={idx}>{name}</option>
              ))}
            </select>
            <label className="text-green-700 dark:text-green-300 font-medium">Rok:</label>
            <select
              value={mYr}
              onChange={e => setMYr(Number(e.target.value))}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            >
              {Array.from({ length: 8 }, (_, i) => currentYear - 5 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </>
        )}
        {tab === "yearly" && (
          <>
            <label className="text-green-700 dark:text-green-300 font-medium">Rok:</label>
            <select
              value={ySel}
              onChange={e => setYSel(Number(e.target.value))}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            >
              {Array.from({ length: 8 }, (_, i) => currentYear - 5 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </>
        )}
      </div>
      <div className="mb-3 text-center text-base text-emerald-800 dark:text-emerald-200 font-semibold">
        Zakres: {formatRange(start, end)}
      </div>
      <div className="flex justify-center mb-6 space-x-4">
        {[
          { key: "weekly", label: "Tygodniowe" },
          { key: "monthly", label: "Miesięczne" },
          { key: "yearly", label: "Roczne" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-full border text-base font-semibold shadow-sm transition
              ${tab === key
                ? "bg-green-600 text-white border-green-600"
                : "bg-white text-green-700 border-green-200 dark:bg-gray-900 dark:text-green-200"}
            `}
          >
            {label}
          </button>
        ))}
      </div>
      {sections.map((sec) =>
        questionsBySection[sec]?.length ? (
          <div key={sec} className="mb-10">
            <h2 className="text-2xl font-bold text-green-700 dark:text-green-300 mb-3">{sec}</h2>
            {questionsBySection[sec].map(renderCard)}
          </div>
        ) : null
      )}
    </div>
  );
}
